import {
  LanguageSplitter,
  PracticeFormAnswerField,
  PracticeFormAnswerOption,
} from "@physiofit-digital/physiofit_types";
import styled from "styled-components";
import { RadioButtonWithLabel } from "../../../../core/components/buttons/RadioButtonWithLabel";
import { TextInput } from "../../../../core/components/input/TextInput";
import { getUserAnswerToQuestion } from "../../../../core/store/currentFormSlice";
import { useMainStore } from "../../../../core/store/mainStore";
import { useLanguageSplitter } from "../../../../core/translations/useLanguageSplitter";
import { FormAnswer } from "../FormAnswer";

type Props = {
  answerId: string;
  questionId: string;
  options?: PracticeFormAnswerOption[];
  possibleFields: PracticeFormAnswerField[];
  level?: number;
  isMultipleSelection?: boolean;
};

export const MultipleChoiceAnswer = ({
  answerId,
  options,
  possibleFields,
  level,
  questionId,
  isMultipleSelection,
}: Props) => {
  const languageSplitter = useLanguageSplitter();
  const selectedFieldIds = useMainStore((state) =>
    getUserAnswerToQuestion(state, answerId),
  )?.fieldIds;

  const setUserAnswerToQuestion = useMainStore(
    (state) => state.setUserAnswerToQuestion,
  );

  const deleteUserAnswerToQuestion = useMainStore(
    (state) => state.deleteUserAnswerToQuestion,
  );

  const otherText =
    selectedFieldIds?.find((ans) => ans.isOther)?.otherText || "";

  const handleSelect = (selectedFieldId: string) => {
    const isValueAlreadySelected =
      !selectedFieldIds || selectedFieldIds.length === 0
        ? false
        : selectedFieldIds?.some((ans) => ans.value === selectedFieldId);

    let updatedAnswers: {
      value: string;
      isOther: boolean;
      otherText?: string;
    }[] = [];

    if (isValueAlreadySelected) {
      // Deselect the value
      updatedAnswers = selectedFieldIds?.filter(
        (ans) => ans.value !== selectedFieldId,
      )!;
    } else {
      // Select the value
      updatedAnswers = isMultipleSelection
        ? [
            ...(selectedFieldIds || []),
            { value: selectedFieldId, isOther: false },
          ]
        : [{ value: selectedFieldId, isOther: false }];
    }

    if (updatedAnswers.length === 0) {
      deleteUserAnswerToQuestion(questionId, answerId);
      return;
    }

    const patientAnswer = {
      id: answerId,
      fieldIds: updatedAnswers,
    };
    setUserAnswerToQuestion(questionId, patientAnswer, answerId);
  };

  const handleAddOtherText = (otherTextValue: string) => {
    // if there is any other answer with flag isOther true, remove it
    let updatedAnswers =
      selectedFieldIds?.filter((ans) => ans.isOther === false) || [];

    if (otherTextValue.length > 0) {
      if (isMultipleSelection) {
        updatedAnswers.push({
          value: "other",
          isOther: true,
          otherText: otherTextValue,
        });
      } else {
        updatedAnswers = [
          {
            value: "other",
            isOther: true,
            otherText: otherTextValue,
          },
        ];
      }
    }
    const patientAnswer = {
      id: answerId,
      fieldIds: updatedAnswers,
    };
    setUserAnswerToQuestion(questionId, patientAnswer, answerId);
  };

  return (
    <FieldsContainer>
      {possibleFields.map((field, index) => {
        const fieldValue = field.value as LanguageSplitter;
        const isSelected =
          selectedFieldIds &&
          selectedFieldIds.some((ans) => ans.value === field.fieldId);

        return (
          <span key={field.fieldId}>
            <RadioButtonWithLabel
              isChecked={isSelected}
              text={languageSplitter(fieldValue)}
              onPress={() => handleSelect(field.fieldId)}
              checkBoxIconName={
                isMultipleSelection
                  ? "CheckBoxSquareInactive"
                  : "CheckBoxRoundInactive"
              }
            />
            {options?.includes("OtherOption") &&
              index === possibleFields.length - 1 && (
                <TextInput
                  placeholder="Eigene Antwort"
                  value={otherText}
                  onChange={(text) => handleAddOtherText(text.trim())}
                />
              )}
            {field.children && isSelected && (
              <ChildrenContainer>
                <FormAnswer
                  questionId={questionId}
                  answer={field.children}
                  level={level ? level + 1 : 1}
                />
              </ChildrenContainer>
            )}
          </span>
        );
      })}
    </FieldsContainer>
  );
};

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChildrenContainer = styled.div`
  padding-top: 10px;
  padding-left: 20px;
`;
